import { motion, useInView } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import {
  ShieldCheck,
  Fingerprint,
  CreditCard,
  ScanFace,
  Building2,
  Zap,
  CheckCircle2,
  Lock,
  Clock,
} from 'lucide-react';

const verificationSteps = [
  {
    id: 'aadhaar',
    icon: Fingerprint,
    title: 'Aadhaar e-KYC',
    subtitle: 'UIDAI OTP · XML / DigiLocker',
    desc: 'Verify identity in seconds with consent-based Aadhaar authentication — no branch visit.',
    accent: 'from-blue-500/20 to-cyan-500/10',
    border: 'border-blue-500/30',
    iconColor: 'text-blue-400',
  },
  {
    id: 'pan',
    icon: CreditCard,
    title: 'PAN verification',
    subtitle: 'Income Tax · name match',
    desc: 'Instant PAN validation cross-checked with your Aadhaar name for regulatory compliance.',
    accent: 'from-violet-500/20 to-purple-500/10',
    border: 'border-violet-500/30',
    iconColor: 'text-violet-400',
  },
  {
    id: 'face',
    icon: ScanFace,
    title: 'Live face match',
    subtitle: 'Liveness · anti-spoof',
    desc: 'Real-time selfie matched against Aadhaar photo with liveness detection to block fraud.',
    accent: 'from-[#9C7941]/25 to-[#EBD38D]/10',
    border: 'border-[#9C7941]/40',
    iconColor: 'text-[#EBD38D]',
  },
  {
    id: 'bank',
    icon: Building2,
    title: 'Bank account',
    subtitle: 'Penny drop · IFSC',
    desc: 'Link your INR payout account with penny-drop verification so deposits and withdrawals stay in your name.',
    accent: 'from-green-500/20 to-emerald-500/10',
    border: 'border-green-500/30',
    iconColor: 'text-green-400',
  },
];

const flowLabels = ['Aadhaar', 'PAN', 'Face', 'Bank', 'Verified'];

const trustPoints = [
  { icon: Clock, label: 'Under 2 minutes', desc: 'Fully digital — no paperwork' },
  { icon: Lock, label: 'Encrypted end-to-end', desc: 'Data never stored on device' },
  { icon: ShieldCheck, label: 'CKYC-ready', desc: 'Built for Indian regulations' },
];

export const InstantKyc = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-80px' });
  const [activeStep, setActiveStep] = useState(0);
  const [demoComplete, setDemoComplete] = useState(false);

  useEffect(() => {
    if (!isInView) return undefined;
    const interval = setInterval(() => {
      setActiveStep((prev) => {
        if (prev >= flowLabels.length - 1) {
          setDemoComplete(true);
          return prev;
        }
        return prev + 1;
      });
    }, 1400);
    return () => clearInterval(interval);
  }, [isInView]);

  useEffect(() => {
    if (!isInView) return;
    setActiveStep(0);
    setDemoComplete(false);
  }, [isInView]);

  return (
    <section
      id="instant-kyc"
      ref={ref}
      className="relative py-24 md:py-32 overflow-hidden bg-[#0a0b0d]"
      data-testid="instant-kyc-section"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-[480px] h-[480px] bg-[#9C7941]/8 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-[520px] h-[360px] bg-blue-500/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 xl:px-16 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-14 md:mb-16"
        >
          <span className="inline-flex items-center gap-2 bg-[#9C7941]/10 border border-[#9C7941]/30 px-4 py-2 rounded-full text-sm text-[#EBD38D] font-medium mb-5">
            <Zap size={16} />
            Instant KYC · India
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
            Verify in minutes —
            <span className="text-[#EBD38D]"> not days</span>
          </h2>
          <p className="text-[#9A9B9F] text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            BITZX Exchange uses instant digital KYC: Aadhaar and PAN checks, live face match,
            and bank verification — so you can trade and move INR with confidence.
          </p>
        </motion.div>

        {/* Live demo progress strip */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-12 max-w-3xl mx-auto"
        >
          <div className="rounded-2xl border border-[#2a2d35] bg-[#12141a]/90 p-5 md:p-6 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-[#6B6D75]">
                Live verification flow
              </p>
              <span
                className={`text-xs font-bold px-2.5 py-1 rounded-full ${
                  demoComplete
                    ? 'bg-green-500/15 text-green-400 border border-green-500/30'
                    : 'bg-[#9C7941]/15 text-[#EBD38D] border border-[#9C7941]/30'
                }`}
              >
                {demoComplete ? 'KYC approved' : 'In progress…'}
              </span>
            </div>
            <div className="flex items-center justify-between gap-1 md:gap-2">
              {flowLabels.map((label, i) => {
                const done = i < activeStep || demoComplete;
                const current = i === activeStep && !demoComplete;
                return (
                  <div key={label} className="flex flex-col items-center flex-1 min-w-0">
                    <div
                      className={`w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center border-2 transition-all duration-500 ${
                        done
                          ? 'bg-green-500/20 border-green-500 text-green-400'
                          : current
                            ? 'bg-[#9C7941]/20 border-[#EBD38D] text-[#EBD38D] scale-110'
                            : 'bg-[#1a1d24] border-[#2a2d35] text-[#4A4B50]'
                      }`}
                    >
                      {done ? (
                        <CheckCircle2 size={18} />
                      ) : (
                        <span className="text-xs font-bold">{i + 1}</span>
                      )}
                    </div>
                    <p
                      className={`mt-2 text-[10px] md:text-xs font-medium truncate w-full text-center ${
                        done || current ? 'text-white' : 'text-[#6B6D75]'
                      }`}
                    >
                      {label}
                    </p>
                    {i < flowLabels.length - 1 && (
                      <div
                        className="hidden md:block absolute"
                        aria-hidden
                      />
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 h-1.5 rounded-full bg-[#1a1d24] overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#9C7941] to-[#EBD38D] rounded-full"
                initial={{ width: '0%' }}
                animate={{
                  width: demoComplete
                    ? '100%'
                    : `${Math.min(100, (activeStep / (flowLabels.length - 1)) * 100)}%`,
                }}
                transition={{ duration: 0.5 }}
              />
            </div>
          </div>
        </motion.div>

        {/* Verification cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5 mb-12">
          {verificationSteps.map((step, i) => (
            <motion.div
              key={step.id}
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.15 + i * 0.08, duration: 0.5 }}
              className={`relative rounded-2xl border ${step.border} bg-gradient-to-br ${step.accent} bg-[#12141a]/80 p-6 hover:scale-[1.02] transition-transform`}
            >
              <div className="w-12 h-12 rounded-xl bg-[#0a0b0d]/60 border border-white/5 flex items-center justify-center mb-4">
                <step.icon size={24} className={step.iconColor} />
              </div>
              <p className="text-[10px] font-bold uppercase tracking-wider text-[#6B6D75] mb-1">
                {step.subtitle}
              </p>
              <h3 className="text-white font-bold text-lg mb-2">{step.title}</h3>
              <p className="text-[#9A9B9F] text-sm leading-relaxed">{step.desc}</p>
              <div className="mt-4 flex items-center gap-1.5 text-green-400 text-xs font-semibold">
                <CheckCircle2 size={14} />
                Instant · automated
              </div>
            </motion.div>
          ))}
        </div>

        {/* Trust row */}
        <div className="grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
          {trustPoints.map((item, i) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 12 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.4 + i * 0.08 }}
              className="flex items-center gap-4 p-4 rounded-xl bg-[#12141a]/60 border border-[#2a2d35]"
            >
              <div className="w-10 h-10 rounded-lg bg-[#9C7941]/10 flex items-center justify-center shrink-0">
                <item.icon size={20} className="text-[#EBD38D]" />
              </div>
              <div>
                <p className="text-white font-bold text-sm">{item.label}</p>
                <p className="text-[#6B6D75] text-xs">{item.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ delay: 0.6 }}
          className="text-center text-[#6B6D75] text-xs mt-10 max-w-2xl mx-auto"
        >
          Showcase demonstrates the planned BITZX verification journey. Final providers and
          timelines may vary by jurisdiction; KYC is required before INR deposit, withdrawal, and
          higher trading limits.
        </motion.p>
      </div>
    </section>
  );
};

export default InstantKyc;
